import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import AppShell from '../../chrome/AppShell';
import useUserOrg from '../../../Authentication/components/hooks/useUserOrg';
import useAuthenticatedApi from '../../../Authentication/components/hooks/useAuthenticatedApi';
import useDeviceKeys from '../../../DeviceManagement/components/hooks/useDeviceKeys';
import { ConfirmDialog, Crumb, Dialog, Field, Select } from '../../components/forms';
import { PageHeadActions } from '../../components/nav';
import { TCard, TEmpty, TFoot, TRow, TSkeleton } from '../../components/table';
import { useToast } from '../../components/roster';
import { seen } from './shared';
import './devices.css';

/*
 * One device group's keys. Also mounted at …/device-keys/assign,
 * which opens the "Add key" dialog on arrival.
 */

export default function DeviceGroupKeysV3() {
    const navigate = useNavigate();
    const location = useLocation();
    const { groupId } = useParams();
    const gid = +(groupId || 0);
    const { orgId } = useUserOrg();
    const api = useAuthenticatedApi();
    const { getDeviceKeys, getDeviceKeysForGroup } = useDeviceKeys(orgId);
    const toast = useToast();
    const listUrl = `/device-management/device-groups/${groupId}/device-keys`;

    const [group, setGroup] = useState<DeviceGroup | null>(null);
    const [keys, setKeys] = useState<DeviceKey[] | null>(null);
    const [failed, setFailed] = useState(false);

    const load = useCallback(() => {
        if (!orgId) return;
        setFailed(false);
        getDeviceKeysForGroup(gid)
            .then((d: { group: DeviceGroup; keys: DeviceKey[] }) => {
                setGroup(d?.group ?? null);
                setKeys(Array.isArray(d?.keys) ? d.keys : []);
            })
            .catch(() => setFailed(true));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orgId, gid]);
    useEffect(load, [load]);

    /* remove from group */
    const [removing, setRemoving] = useState<DeviceKey | null>(null);
    const remove = async (k: DeviceKey) => {
        const before = keys;
        setKeys((list) => (list ?? []).filter((r) => r.id !== k.id));
        try {
            const res = await api.delete(`${orgId}/device-groups/${groupId}/${k.id}`);
            if (res.status !== 200) throw new Error(String(res.status));
            toast.show(`Removed ${k.name} from the group`);
        } catch {
            setKeys(before);
            toast.show(`Couldn’t remove ${k.name}. Try again.`);
        }
    };

    /* add an existing key */
    const assignRoute = location.pathname.endsWith('/assign');
    const [adding, setAdding] = useState(assignRoute);
    const [allKeys, setAllKeys] = useState<DeviceKey[] | null>(null);
    const [allFailed, setAllFailed] = useState(false);
    const [pick, setPick] = useState('');
    const [pickError, setPickError] = useState('');
    const [assignError, setAssignError] = useState('');
    const [assigning, setAssigning] = useState(false);
    const busy = useRef(false);

    useEffect(() => {
        if (assignRoute) setAdding(true);
    }, [assignRoute]);

    useEffect(() => {
        if (!adding || !orgId) return;
        let live = true;
        setAllFailed(false);
        getDeviceKeys()
            .then((list: DeviceKey[]) => live && setAllKeys(Array.isArray(list) ? list : []))
            .catch(() => live && setAllFailed(true));
        return () => {
            live = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [adding, orgId]);

    const inGroup = new Set((keys ?? []).map((k) => k.id));
    const assignable = (allKeys ?? []).filter((k) => !inGroup.has(k.id));

    const closeAdd = useCallback(() => {
        if (busy.current) return;
        setAdding(false);
        setPick('');
        setPickError('');
        setAssignError('');
        if (assignRoute) navigate(listUrl, { replace: true });
    }, [assignRoute, listUrl, navigate]);

    const assign = async () => {
        if (busy.current) return;
        if (!pick) {
            setPickError('Choose a device key.');
            return;
        }
        busy.current = true;
        setAssigning(true);
        setAssignError('');
        try {
            const res = await api.post(
                `${orgId}/device-groups/${groupId}/${pick}`,
                JSON.stringify({ action: 'assign' }),
            );
            if (res.status !== 200) throw new Error(String(res.status));
            const k = assignable.find((x) => String(x.id) === pick);
            busy.current = false;
            closeAdd();
            toast.show(`Added ${k?.name ?? 'device key'} to the group`);
            load();
        } catch {
            setAssignError('Couldn’t add the key. Try again.');
        } finally {
            busy.current = false;
            setAssigning(false);
        }
    };

    const n = keys?.length ?? 0;

    return (
        <AppShell>
            <div className="dv-page">
                <Crumb label="Devices" onClick={() => navigate('/device-management#groups')} />
                <PageHeadActions
                    title={group?.name ?? (failed ? 'Device group' : ' ')}
                    sub={keys ? `${n} ${n === 1 ? 'device' : 'devices'} in this group` : 'Device group'}
                    actions={
                        <>
                            <button
                                className="tb-btn"
                                onClick={() => navigate(`/device-management/device-groups/${groupId}/invites`)}
                            >
                                Invites
                            </button>
                            <button
                                className="tb-btn"
                                onClick={() => navigate(`/device-management/device-groups/${groupId}/device-keys/create`)}
                            >
                                New key
                            </button>
                            <button className="tb-new" onClick={() => setAdding(true)} disabled={!keys}>
                                Add key
                            </button>
                        </>
                    }
                />

                {failed ? (
                    <TCard>
                        <TEmpty message="Couldn’t load this group." actionLabel="Retry" onAction={load} />
                    </TCard>
                ) : keys === null ? (
                    <TCard>
                        <TSkeleton rows={4} />
                    </TCard>
                ) : keys.length === 0 ? (
                    <TCard>
                        <TEmpty
                            message="No devices in this group yet."
                            actionLabel="Add key"
                            onAction={() => setAdding(true)}
                        />
                    </TCard>
                ) : (
                    <TCard settle>
                        {keys.map((k, i) => {
                            const tele = `/orgs/${orgId}/device-telemetry/${k.keyId}`;
                            return (
                                <TRow
                                    key={k.id}
                                    index={i}
                                    name={k.name}
                                    meta={k.keyId}
                                    metaDate={seen(k.lastUsed)}
                                    date={seen(k.lastUsed)}
                                    status=""
                                    href={tele}
                                    onOpen={() => navigate(tele)}
                                    menuItems={[
                                        { label: 'Telemetry', onClick: () => navigate(tele) },
                                        { label: 'Remove from group…', onClick: () => setRemoving(k) },
                                    ]}
                                />
                            );
                        })}
                    </TCard>
                )}
                {keys && keys.length > 0 && <TFoot>{`${n} ${n === 1 ? 'device' : 'devices'}`}</TFoot>}
            </div>

            <Dialog
                open={adding}
                title={group?.name ? `Add a key to ${group.name}` : 'Add a key to this group'}
                onClose={closeAdd}
                onSubmit={assign}
                footer={
                    <>
                        <button type="button" className="tb-btn" onClick={closeAdd} disabled={assigning}>
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="tb-new"
                            disabled={assigning || allKeys === null || assignable.length === 0}
                        >
                            {assigning ? 'Adding…' : 'Add key'}
                        </button>
                    </>
                }
            >
                {allFailed ? (
                    <p className="banner dv-note err" role="alert">
                        Couldn’t load your device keys. Close and try again.
                    </p>
                ) : allKeys === null || keys === null ? (
                    <p style={{ margin: 0 }}>Loading device keys…</p>
                ) : assignable.length === 0 ? (
                    <p style={{ margin: 0 }}>
                        Every device key is already in this group. Create a new key to add another device.
                    </p>
                ) : (
                    <Field label="Device key" help="Keys already in this group aren’t listed." error={pickError}>
                        <Select
                            autoFocus
                            value={pick}
                            disabled={assigning}
                            aria-invalid={!!pickError}
                            onChange={(e) => {
                                setPick(e.target.value);
                                setPickError('');
                            }}
                        >
                            <option value="">Choose a key</option>
                            {assignable.map((k) => (
                                <option key={k.id} value={String(k.id)}>
                                    {k.name} ({k.keyId})
                                </option>
                            ))}
                        </Select>
                    </Field>
                )}
                {assignError && (
                    <p className="banner dv-note err" role="alert" style={{ margin: '12px 0 0' }}>
                        {assignError}
                    </p>
                )}
            </Dialog>

            <ConfirmDialog
                open={removing !== null}
                title={`Remove “${removing?.name ?? ''}” from this group?`}
                body="The device leaves this group. Its key stays active, and you can add it back later."
                actionLabel="Remove from group"
                onConfirm={() => removing && remove(removing)}
                onClose={() => setRemoving(null)}
            />
            {toast.node}
        </AppShell>
    );
}
